import React, {createContext, useContext, useEffect, useState} from "react";
import ApiInterface from "@/commons/api/ApiInterface";
import Cookies from "js-cookie";
import {environment} from "@/commons/configs/Config";
import {getToken} from "@/commons/utils/others/Account";
import {getDetails} from "@/commons/api/apiPages/Account";

type StaffGroupType = {
    id: number,
    groupPower: number,
    groupName: string,
    groupDescription: string,
    iconUUID: string,
    staffer: boolean
}

type AccountDataType = {
    id: number,
    uid: string,
    username: string,
    email: string,
    minecraftUid: string,
    twitchId: string,
    staffGroup: StaffGroupType,
    emailVerified: boolean,
    lastKnowIp: string
}

type MinecraftDataType = {
    authCode: string,
    firstLogin: number,
    lastLogout: number,
    pastUsernames: [],
    username: string,
    uuid: string,
}

type UserContextType = {
    isLogged: boolean;
    isStaff: boolean;
    userLoadingComplete: boolean;
    minecraftData: MinecraftDataType | undefined;
    accountData: AccountDataType | undefined;
};

const UserContext = createContext<UserContextType>({
    isLogged: false,
    isStaff: false,
    userLoadingComplete: false,
    minecraftData: undefined,
    accountData: undefined,
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const [isLogged, setIsLogged] = useState(false);
    const [isStaff, setIsStaff] = useState(false);
    const [minecraftData, setMinecraftData] = useState<MinecraftDataType | undefined>(undefined);
    const [accountData, setAccountData] = useState<AccountDataType | undefined>(undefined);
    const [userLoadingComplete, setUserLoadingComplete] = useState(false);

    let token = getToken();

    async function loadInfo() {
        try {
            const logged = await ApiInterface.checkLogged();
            setIsLogged(logged);

            if (logged) {
                const staff = await ApiInterface.isStaffer();
                setIsStaff(staff);

                await loadUserInfo();
                await loadMinecraftInfo()
            }
        } catch (err) {
            setIsLogged(false);
            setIsStaff(false);
        } finally {
            setUserLoadingComplete(true);
        }
    }

    async function loadMinecraftInfo() {
        let mcData = await getMinecraft(token);
        if(mcData === null) return;
        if (mcData.status === 200) {
            let json = await mcData.json();

            let converted: MinecraftDataType = {
                authCode: json.authCode ?? "",
                firstLogin: json.firstLogin ?? 0,
                lastLogout: json.lastLogout ?? 0,
                pastUsernames: Array.isArray(json.pastUsernames) ? json.pastUsernames : [],
                username: json.username ?? "",
                uuid: json._id ?? "",
            };
            setMinecraftData(converted);
        }
        return
    }

    async function loadUserInfo(){
        let account = await getDetails(token);
        if(account === null) return;
        if(account.status !== 200) return;

        let json = await account.json();
        setAccountData(convertAccountData(json));
        return
    }
    function convertAccountData(json: any): AccountDataType {
        return {
            id: json.id ?? 0,
            uid: json.uid ?? "",
            username: json.username ?? "",
            email: json.email ?? "",
            minecraftUid: json.minecraftUid ?? "",
            twitchId: json.twitchId ?? "",
            staffGroup: {
                id: json.staffGroup?.id ?? 0,
                groupPower: json.staffGroup?.groupPower ?? 0,
                groupName: json.staffGroup?.groupName ?? "",
                groupDescription: json.staffGroup?.groupDescription ?? "",
                iconUUID: json.staffGroup?.iconUUID ?? "",
                staffer: json.staffGroup?.staffer ?? false
            },
            emailVerified: json.emailVerified ?? false,
            lastKnowIp: json.lastKnowIp ?? "",
        };
    }

    function preLoad() {
        if (localStorage.token !== undefined) {
            localStorage.removeItem(environment.tokenName);
        }
        if (localStorage.cookiesAccepted !== undefined) {
            localStorage.removeItem("cookiesAccepted");
        }

        const token = Cookies.get(environment.tokenName);
        if (token !== undefined) {
            Cookies.set('token', token, {expires: 30, path: '/', domain: 'eblcraft.it'});
        }

        const cookiesAccepted = Cookies.get("cookiesAccepted");
        if (cookiesAccepted !== undefined) {
            Cookies.set('cookiesAccepted', cookiesAccepted, {expires: 30, path: '/', domain: 'eblcraft.it'});
        }
    }

    useEffect(() => {
        preLoad();
        loadInfo();
    }, []);

    return (
        <UserContext.Provider value={{isLogged, isStaff, userLoadingComplete, minecraftData, accountData}}>
            {children}
        </UserContext.Provider>
    );
};


async function getMinecraft(token: string | undefined) {
    if (token === null || token === undefined || token === "") {
        return null;
    }

    const result = fetch(environment.apiUrlOne + '/details/minecraft', {
        method: 'GET',
        headers: {
            'Authorization': token
        }
    })

    const rest = await result;
    return rest
}